import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { collection, addDoc, getDocs, query, where } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { createUserWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { showSuccess, showError, showWarning, showInfo } from '../utils/toast';

const StudentRegistration = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [showOtherField, setShowOtherField] = useState(false);
  const [googleUser, setGoogleUser] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    organizationId: '',
    organizationName: '',
    otherOrganization: '',
    city: '',
    state: '',
    country: 'India'
  });

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'organizationAdmins'));
      const orgList = [];
      querySnapshot.forEach((doc) => {
        orgList.push({
          id: doc.id,
          name: doc.data().organizationName
        });
      });
      setOrganizations(orgList);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'organizationId') {
      if (value === 'other') {
        setShowOtherField(true);
        setFormData(prev => ({ ...prev, organizationName: 'Other' }));
      } else {
        setShowOtherField(false);
        const selectedOrg = organizations.find(org => org.id === value);
        setFormData(prev => ({ 
          ...prev, 
          organizationName: selectedOrg ? selectedOrg.name : '',
          otherOrganization: ''
        }));
      }
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Check if user already exists
      const studentQuery = query(collection(db, 'students'), where('email', '==', user.email));
      const studentSnapshot = await getDocs(studentQuery);

      if (!studentSnapshot.empty) {
        showError('Account already exists! Please login.');
        await auth.signOut();
        setLoading(false);
        navigate('/student-login');
        return;
      }

      // Pre-fill form with Google data
      setGoogleUser(user);
      setFormData(prev => ({
        ...prev,
        name: user.displayName || '',
        email: user.email
      }));
      
      showSuccess('Google account connected! Please complete your profile.');
    } catch (error) {
      console.error('Error with Google sign-in:', error);
      showError('Failed to sign in with Google. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.phoneNumber) {
      showWarning('Please fill in all required fields');
      return;
    }

    if (!googleUser) {
      if (!formData.password || formData.password.length < 6) {
        showWarning('Password must be at least 6 characters');
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        showWarning('Passwords do not match');
        return;
      }
    }

    if (!formData.organizationId) {
      showWarning('Please select an organization');
      return;
    }

    if (showOtherField && !formData.otherOrganization) {
      showWarning('Please enter your organization name');
      return;
    }

    setLoading(true);

    try {
      let userId;

      // Create Firebase Auth user if not using Google
      if (!googleUser) {
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          formData.email,
          formData.password
        );
        userId = userCredential.user.uid;
      } else {
        userId = googleUser.uid;
      }

      // Determine approval status
      let approvalStatus = 'active'; // Default for "Other" organization
      let needsApproval = false;

      if (formData.organizationId !== 'other') {
        approvalStatus = 'pending';
        needsApproval = true;
      }

      // Create student document
      const studentData = {
        uid: userId,
        name: formData.name,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        organizationId: formData.organizationId === 'other' ? null : formData.organizationId,
        organizationName: showOtherField ? formData.otherOrganization : formData.organizationName,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        approvalStatus: approvalStatus, // 'pending', 'approved', 'rejected', 'active'
        registrationMethod: googleUser ? 'google' : 'email',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await addDoc(collection(db, 'students'), studentData);

      // Success message based on approval requirement
      if (needsApproval) {
        showSuccess('Registration successful! Waiting for organization admin approval.');
        setTimeout(() => {
          showInfo('You will receive access once your organization admin approves your request.');
        }, 1500);
      } else {
        showSuccess('Registration successful! You can now login.');
      }

      // Sign out and redirect to login
      await auth.signOut();
      setTimeout(() => {
        navigate('/student-login');
      }, 2000);

    } catch (error) {
      console.error('Error registering student:', error);
      
      if (error.code === 'auth/email-already-in-use') {
        showError('Email already registered. Please login instead.');
      } else if (error.code === 'auth/invalid-email') {
        showError('Invalid email address.');
      } else if (error.code === 'auth/weak-password') {
        showError('Password is too weak. Use at least 6 characters.');
      } else {
        showError('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-8 text-white text-center">
            <h1 className="text-3xl font-bold mb-2">Student Registration</h1>
            <p className="text-indigo-100">Create your account to access exams</p>
          </div>

          <div className="p-8">
            {/* Google Sign In Button */}
            {!googleUser && (
              <div className="mb-6">
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 hover:border-indigo-500 transition disabled:opacity-50"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </button>
                
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500">Or register with email</span>
                  </div>
                </div>
              </div>
            )}

            {googleUser && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 font-medium flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Google account connected! Please complete your profile below.
                </p>
              </div>
            )}

            {/* Registration Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                  placeholder="Enter your full name"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  disabled={!!googleUser}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition disabled:bg-gray-100"
                  placeholder="your.email@example.com"
                />
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phoneNumber"
                  required
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                  placeholder="+91 1234567890"
                />
              </div>

              {/* Organization Dropdown with Integrated Search */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  College / Organization *
                </label>
                <select
                  name="organizationId"
                  required
                  value={formData.organizationId}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                >
                  <option value="">Select your organization</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                  <option value="other">Other (Not listed)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Select your college or organization from the list
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.organizationId === 'other' 
                    ? 'Your account will be active immediately for public exams'
                    : formData.organizationId 
                    ? 'Your organization admin will approve your request'
                    : 'Select your college or organization from the list'}
                </p>
              </div>

              {/* Other Organization Field */}
              {showOtherField && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Enter Organization Name *
                  </label>
                  <input
                    type="text"
                    name="otherOrganization"
                    required={showOtherField}
                    value={formData.otherOrganization}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                    placeholder="Your college/organization name"
                  />
                </div>
              )}

              {/* Location Fields */}
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    name="city"
                    required
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    State *
                  </label>
                  <input
                    type="text"
                    name="state"
                    required
                    value={formData.state}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                    placeholder="State"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Country *
                  </label>
                  <input
                    type="text"
                    name="country"
                    required
                    value={formData.country}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                    placeholder="Country"
                  />
                </div>
              </div>

              {/* Password Fields (only if not using Google) */}
              {!googleUser && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Password *
                    </label>
                    <input
                      type="password"
                      name="password"
                      required={!googleUser}
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                      placeholder="At least 6 characters"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Confirm Password *
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      required={!googleUser}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                      placeholder="Re-enter password"
                    />
                  </div>
                </>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>

              {/* Login Link */}
              <div className="text-center">
                <p className="text-gray-600">
                  Already have an account?{' '}
                  <Link to="/student-login" className="text-indigo-600 font-semibold hover:text-indigo-800">
                    Login here
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentRegistration;
